import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { DS } from '../../PatientForms/designTokens';
import { Allergy } from '../types';

interface MultipleAllergiesProps {
  allergies: Allergy[];
  setAllergies: (a: Allergy[]) => void;
  handleFormSubmit: (e: React.FormEvent) => void;
}

const MultipleAllergies: React.FC<MultipleAllergiesProps> = ({
  allergies,
  setAllergies,
  handleFormSubmit,
}) => {
  const [addMoreMode, setAddMoreMode] = useState<null | boolean>(null);
  const [shouldSubmit, setShouldSubmit] = useState(false);

  const handleInputChange = (
    index: number,
    field: keyof Allergy,
    value: string
  ) => {
    const updated = [...allergies];
    updated[index][field] = value;
    setAllergies(updated);
  };

  const handleAddNewSet = () => {
    const last = allergies[allergies.length - 1];
    if (last && (!last.allergen.trim() || !last.reaction.trim())) return;

    setAllergies([...allergies, { allergen: '', reaction: '' }]);
  };

  const handleAddMoreRadioChange = (value: 'Ja' | 'Nej') => {
    if (value === 'Ja') {
      handleAddNewSet();
      setAddMoreMode(true);
    } else {
      setAddMoreMode(false);
      setAllergies(allergies.slice(0, 1));
    }
    setShouldSubmit(true);
  };

  useEffect(() => {
    if (shouldSubmit) {
      handleFormSubmit(new Event('submit') as unknown as React.FormEvent);
      setShouldSubmit(false);
    }
  }, [shouldSubmit, allergies, handleFormSubmit]);

  useEffect(() => {
    if (allergies.length > 1 && addMoreMode !== true) {
      setAddMoreMode(true);
    }
  }, [allergies, addMoreMode]);

  return (
    <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
      {allergies.map((allergy, index) => (
        // eslint-disable-next-line react/no-array-index-key -- entries have no id; the list is only appended to, edited in place or truncated from the end
        <Box key={index}>
          <Typography
            sx={{
              fontWeight: 600,
              color: '#374151',
              mb: 1,
              fontFamily: DS.fontBody,
              fontSize: '16px',
            }}
          >
            Vilket/vilka:
          </Typography>
          <input
            type="text"
            onBlur={handleFormSubmit}
            value={allergy.allergen}
            onChange={(e) =>
              handleInputChange(index, 'allergen', e.target.value)
            }
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #D1D5DB',
              borderRadius: '6px',
              fontSize: '16px',
              fontFamily: DS.fontBody,
            }}
          />

          <Typography
            sx={{
              fontWeight: 600,
              color: '#374151',
              mt: 2,
              mb: 1,
              fontFamily: DS.fontBody,
              fontSize: '16px',
            }}
          >
            Vilken reaktion får du?
          </Typography>
          <input
            type="text"
            onBlur={handleFormSubmit}
            value={allergy.reaction}
            onChange={(e) =>
              handleInputChange(index, 'reaction', e.target.value)
            }
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #D1D5DB',
              borderRadius: '6px',
              fontSize: '16px',
              fontFamily: DS.fontBody,
            }}
          />

          {index === 0 && (
            <>
              <Typography
                sx={{
                  fontWeight: 600,
                  color: '#374151',
                  mt: 2,
                  mb: 1,
                  fontFamily: DS.fontBody,
                  fontSize: '16px',
                }}
              >
                Har du någon övrig allergi?
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {['Ja', 'Nej'].map((val) => (
                  <Box
                    component="label"
                    key={val}
                    sx={{ display: 'flex', alignItems: 'center' }}
                  >
                    <input
                      type="radio"
                      name="otherAllergy"
                      value={val}
                      checked={
                        (addMoreMode === true && val === 'Ja') ||
                        (addMoreMode === false && val === 'Nej')
                      }
                      onChange={() =>
                        handleAddMoreRadioChange(val as 'Ja' | 'Nej')
                      }
                      onBlur={handleFormSubmit}
                      style={{ marginRight: '8px' }}
                    />
                    {val}
                  </Box>
                ))}
              </Box>
            </>
          )}
        </Box>
      ))}

      {addMoreMode === true && (
        <Box
          component="button"
          onClick={handleAddNewSet}
          sx={{
            mt: 2,
            py: 1.5,
            px: 2,
            backgroundColor: DS.deepTeal,
            color: '#FFFFFF',
            borderRadius: '6px',
            width: '100%',
            border: 'none',
            cursor: 'pointer',
            fontFamily: DS.fontBody,
            fontSize: '16px',
          }}
        >
          Lägg till ytterligare allergi
        </Box>
      )}
    </Box>
  );
};

export default MultipleAllergies;
